import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { dramas } from "@/mocks/dramas";
import { filterDramas, sortDramas, type SortKey } from "@/lib/data";
import type { Drama } from "@/types";
import PageHeader from "@/components/base/PageHeader";
import DramaCard from "@/components/base/DramaCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import CategoryFilter, { type FilterState } from "@/pages/category/components/CategoryFilter";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((c) => c.type === type);

  const [filter, setFilter] = useState<FilterState>({ region: "", year: "", tag: "" });
  const [sort, setSort] = useState<SortKey>("popularity");
  const [page, setPage] = useState(1);

  usePageMeta(
    `${category ? category.name : "剧集分类"} - 漂亮的保姆影视剧分类`,
    `${category ? category.desc : "按题材浏览影视剧分类。"}在漂亮的保姆查看分类剧集列表、主演阵容与剧情简介，支持地区、年代与标签筛选。`,
    `漂亮的保姆,${category ? category.name : "剧集分类"},电视剧分类,影视剧资讯,剧情介绍`,
  );

  const baseList = useMemo(
    () => (dramas as Drama[]).filter((d) => d.type === type),
    [type],
  );

  const result = useMemo(() => {
    const filtered = filterDramas(baseList, filter);
    return sortDramas(filtered, sort);
  }, [baseList, filter, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = result.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  function reset() {
    setFilter({ region: "", year: "", tag: "" });
    setSort("popularity");
    setPage(1);
  }

  if (!category) {
    return (
      <>
        <PageHeader
          title="分类不存在"
          crumbs={[{ label: "首页", to: "/" }, { label: "剧集分类" }]}
        />
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10">
          <EmptyState
            title="未找到该分类"
            desc="你访问的分类不存在或已下线，可以返回首页或浏览其他分类。"
            actionText="返回首页"
            onAction={() => window.REACT_APP_NAVIGATE("/")}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={category.name}
        desc={category.desc}
        crumbs={[{ label: "首页", to: "/" }, { label: "剧集分类" }, { label: category.name }]}
      />

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <Link
              key={c.type}
              to={`/category/${c.type}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                c.type === type
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300"
              }`}
            >
              <i className={c.icon} />
              {c.name}
            </Link>
          ))}
        </div>

        <CategoryFilter
          filter={filter}
          setFilter={(f) => {
            setFilter(f);
            setPage(1);
          }}
          sort={sort}
          setSort={(s) => {
            setSort(s);
            setPage(1);
          }}
          onReset={reset}
        />

        <div className="flex items-center justify-between mt-6 mb-4">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-primary-600 font-semibold">{result.length}</span> 部
            {category.name}
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {pageItems.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            title="没有符合条件的剧集"
            desc="试试调整筛选条件，或点击下方按钮重置全部筛选。"
            actionText="重置筛选"
            onAction={reset}
          />
        )}
      </div>
    </>
  );
}